import moment from 'moment';
import {
  type FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import type { TimeFormat } from '@app/api/Branches/types';
import { useCheckRecordMoveMutation } from '@app/api/Records/api';
import { WARNING } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { CalendarMoveRecordContext } from '@app/providers/CalendarProvider/CalendarMoveRecordContext';
import type {
  CheckIfCanMoveRecord,
  NotificationModalHandlers,
} from '@app/providers/CalendarProvider/CalendarMoveRecordContext.types';
import { CalendarSettingsContext } from '@app/providers/CalendarProvider/CalendarSettingsContext';
import type { CalendarProviderProps } from '@app/providers/CalendarProvider/CalendarSettingsContext.types';
import { useCalendarAPI } from '@app/providers/CalendarProvider/hooks';
import { IndividualCalendarContext } from '@app/providers/CalendarProvider/IndividualCalendarContext';
import { TeamCalendarContext } from '@app/providers/CalendarProvider/TeamCalendarContext';
import { handleCatchError } from '@app/services';
import type {
  TBranchWorkTimes,
  TEmployee,
  NAnalytics,
  IRecordType,
  NEmployees,
} from '@app/types';
import { generateTimes } from '@app/utils';

type MoveRecordState = {
  canMoveRecord: boolean;
  canMoveMessage: string;
  isModalOpen: boolean;
  notificationModal: NotificationModalHandlers;
};

type MoveRecordAction =
  | { type: 'OPEN_MODAL_SUCCESS'; message: string }
  | { type: 'OPEN_MODAL_FAILURE'; message: string }
  | { type: 'CLOSE_MODAL' }
  | { type: 'SET_SUBMIT_HANDLER'; handler: () => void }
  | { type: 'SET_CANCEL_HANDLER'; handler: () => void };

const moveRecordReducer = (
  state: MoveRecordState,
  action: MoveRecordAction
): MoveRecordState => {
  switch (action.type) {
    case 'OPEN_MODAL_SUCCESS':
      return {
        ...state,
        canMoveRecord: true,
        canMoveMessage: action.message,
        isModalOpen: true,
      };
    case 'OPEN_MODAL_FAILURE':
      return {
        ...state,
        canMoveRecord: false,
        canMoveMessage: action.message,
        isModalOpen: true,
      };
    case 'CLOSE_MODAL':
      return { ...state, isModalOpen: false };
    case 'SET_SUBMIT_HANDLER':
      return {
        ...state,
        notificationModal: {
          ...state.notificationModal,
          submitHandler: action.handler,
        },
      };
    case 'SET_CANCEL_HANDLER':
      return {
        ...state,
        notificationModal: {
          ...state.notificationModal,
          cancelHandler: action.handler,
        },
      };
    default:
      return state;
  }
};

const MOVE_RECORD_INITIAL_STATE: MoveRecordState = {
  canMoveRecord: false,
  canMoveMessage: '',
  isModalOpen: false,
  notificationModal: {
    submitHandler: () => {},
    cancelHandler: () => {},
  },
};

export const CalendarProvider: FC<CalendarProviderProps> = ({ children }) => {
  const [query] = useSearchParams();
  const [timeFormat, setTimeFormat] = useState<TimeFormat | null>(null);
  const { getBranchEmployeesData, isGetBranchEmployeesLoading } =
    useCalendarAPI();
  const { userId, database, activeBranchId } = useUserPrint();
  const [checkRecordMove] = useCheckRecordMoveMutation();

  // --- Team calendar state ---
  const [employeeDetails, setEmployeeDetails] = useState<TEmployee[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<TEmployee[]>([]);
  const [allEmployees, setAllEmployees] = useState<TEmployee[]>([]);
  const [branchWorkingHoursForDate, setBranchWorkingHoursForDate] =
    useState<TBranchWorkTimes>({ WorkTimeS: '', WorkTimeE: '' });
  const [financialStatistics, setFinancialStatistics] =
    useState<NAnalytics.ModelFinancialStatistic>();
  const lastStoredEmployeeDetails = useRef<TEmployee[]>([]);

  // --- Individual calendar state ---
  const [individualCalendarData, setIndividualCalendarData] = useState<
    TEmployee[]
  >([]);
  const [branchWorkingHoursForPeriod, setBranchWorkingHoursForPeriod] =
    useState<TBranchWorkTimes[]>([]);
  const lastStoredIndividualCalendarData = useRef<TEmployee[]>([]);

  // --- Settings state ---
  const [branchEmployees, setBranchEmployees] = useState<
    NEmployees.ModelEmployee[]
  >([]);
  const [initialTimes] = useState<string[]>(generateTimes());
  const [isLoading, setIsLoading] = useState(true);
  const [branchTimezone, setBranchTimezone] = useState<string | null>(null);
  const lastStoredBranchEmployees = useRef<NEmployees.ModelEmployee[]>([]);
  const verticalScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const { showNotification } = useContext(NotificationContext);

  // --- Move record modal state (useReducer for atomic transitions) ---
  const [moveRecordState, dispatchMoveRecord] = useReducer(
    moveRecordReducer,
    MOVE_RECORD_INITIAL_STATE
  );
  const { canMoveRecord, canMoveMessage, isModalOpen, notificationModal } =
    moveRecordState;

  const date = query.get('date');
  const dateString = useMemo(() => {
    return date ?? moment().format('YYYY-MM-DD');
  }, [date]);

  // --- Settings setters ---
  const setLastStoredBranchEmployees = useCallback(
    (branchEmployeesValue: NEmployees.ModelEmployee[]) => {
      lastStoredBranchEmployees.current = branchEmployeesValue;
    },
    []
  );

  // --- Team calendar setters ---
  const setLastStoredEmployeeDetails = useCallback(
    (employeeDetailsValue: TEmployee[]) => {
      lastStoredEmployeeDetails.current = employeeDetailsValue;
    },
    []
  );

  // --- Individual calendar setters ---
  const setLastStoredIndividualCalendarData = useCallback(
    (individualCalendarDataValue: TEmployee[]) => {
      lastStoredIndividualCalendarData.current = individualCalendarDataValue;
    },
    []
  );

  // --- Move record modal setters ---
  const setIsModalOpen = useCallback((isOpen: boolean) => {
    if (!isOpen) dispatchMoveRecord({ type: 'CLOSE_MODAL' });
  }, []);

  const setNotificationModalSubmitHandler = useCallback(
    (handler: () => void) => {
      if (typeof handler === 'function') {
        dispatchMoveRecord({ type: 'SET_SUBMIT_HANDLER', handler });
      }
    },
    []
  );

  const setNotificationModalCancelHandler = useCallback(
    (handler: () => void) => {
      if (typeof handler === 'function') {
        dispatchMoveRecord({ type: 'SET_CANCEL_HANDLER', handler });
      }
    },
    []
  );

  // --- Record update helpers ---
  const findAndReplaceRecordInEmployeeDetails = useCallback(
    (newRecord: IRecordType) => {
      let oldRecord = {} as IRecordType;
      let newRecords = [] as IRecordType[];
      let employeeId = 0;

      for (const employee of employeeDetails) {
        oldRecord = employee.Records.find(
          (record) => record.Id === newRecord.Id
        )!;
        if (oldRecord) {
          newRecords = employee.Records.filter(
            (record) => record.Id !== newRecord.Id
          );
          employeeId = employee.UserId;
          break;
        }
      }

      if (!oldRecord) return employeeDetails;

      return employeeDetails
        .map((employee) => {
          if (employeeId === employee.UserId)
            return { ...employee, Records: newRecords };
          return employee;
        })
        .map((employee) => {
          if (employee.UserId === newRecord.EmployeeId)
            return {
              ...employee,
              Records: [
                ...employee.Records,
                {
                  ...oldRecord,
                  TimeS: newRecord.TimeS,
                  TimeE: newRecord.TimeE,
                  EmployeeId: newRecord.EmployeeId,
                },
              ],
            };
          return employee;
        });
    },
    [employeeDetails]
  );

  const findAndReplaceRecordInIndividualCalendarData = useCallback(
    (newRecord: IRecordType) => {
      let oldRecord = {} as IRecordType;
      let newRecords = [] as IRecordType[];
      let dateWithOldRecords = '';

      for (const employee of individualCalendarData) {
        oldRecord = employee.Records.find(
          (record) => record.Id === newRecord.Id
        )!;
        if (oldRecord) {
          newRecords = employee.Records.filter(
            (record) => record.Id !== newRecord.Id
          );
          dateWithOldRecords = moment(employee.Date).format('YYYY-MM-DD');
          break;
        }
      }

      if (!oldRecord) return individualCalendarData;

      return individualCalendarData
        .map((employee) => {
          if (
            moment(employee.Date).format('YYYY-MM-DD') ===
            moment(dateWithOldRecords).format('YYYY-MM-DD')
          ) {
            return { ...employee, Records: newRecords };
          }
          return employee;
        })
        .map((employee) => {
          if (
            moment(employee.Date).format('YYYY-MM-DD') ===
            moment(newRecord.TimeS).format('YYYY-MM-DD')
          ) {
            return {
              ...employee,
              Records: [
                ...employee.Records,
                {
                  ...oldRecord,
                  TimeS: newRecord.TimeS,
                  TimeE: newRecord.TimeE,
                  EmployeeId: newRecord.EmployeeId,
                },
              ],
            };
          }
          return employee;
        });
    },
    [individualCalendarData]
  );

  const updateCalendarDataWithNewRecord = useCallback(
    ({
      record,
      type = 'calendar',
      updateRef = false,
    }: {
      record: IRecordType;
      type: string;
      updateRef?: boolean;
    }) => {
      if (type === 'calendar') {
        const newEmployeeDetails =
          findAndReplaceRecordInEmployeeDetails(record);
        setEmployeeDetails(newEmployeeDetails);
        if (updateRef) setLastStoredEmployeeDetails(newEmployeeDetails);
      }
      if (type === 'individual') {
        const newIndividualCalendarData =
          findAndReplaceRecordInIndividualCalendarData(record);
        setIndividualCalendarData(newIndividualCalendarData);
        if (updateRef)
          setLastStoredIndividualCalendarData(newIndividualCalendarData);
      }
    },
    [
      findAndReplaceRecordInEmployeeDetails,
      findAndReplaceRecordInIndividualCalendarData,
      setLastStoredEmployeeDetails,
      setLastStoredIndividualCalendarData,
    ]
  );

  const checkIfCanMoveRecord = useCallback<CheckIfCanMoveRecord>(
    async ({ type, newRecord, onTrueCallback, onFalseCallback }) => {
      try {
        const res = await checkRecordMove({
          ActiveBranchId: activeBranchId,
          data: newRecord,
        }).unwrap();

        if (res.CanMove) {
          if (typeof onTrueCallback === 'function') {
            onTrueCallback(res);
          } else {
            dispatchMoveRecord({
              type: 'OPEN_MODAL_SUCCESS',
              message: res.Message || '',
            });
          }
        } else {
          dispatchMoveRecord({
            type: 'OPEN_MODAL_FAILURE',
            message: res.Message || '',
          });

          if (typeof onFalseCallback === 'function') {
            onFalseCallback();
          } else {
            dispatchMoveRecord({
              type: 'SET_SUBMIT_HANDLER',
              handler: () => {
                dispatchMoveRecord({ type: 'CLOSE_MODAL' });
                if (type === 'individual') {
                  setIndividualCalendarData(
                    lastStoredIndividualCalendarData.current
                  );
                } else {
                  setEmployeeDetails(lastStoredEmployeeDetails.current);
                }
              },
            });
          }
        }
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      }
    },
    [activeBranchId, showNotification, checkRecordMove]
  );

  if (lastStoredEmployeeDetails.current.length === 0) {
    setLastStoredEmployeeDetails(employeeDetails);
  }

  useEffect(() => {
    if (userId && database && activeBranchId) {
      getBranchEmployeesData({
        userId,
        database,
        activeBranchId,
        onSuccess: (data: NEmployees.ModelEmployee[]) =>
          setBranchEmployees(data),
      });
    }
  }, [userId, database, activeBranchId, getBranchEmployeesData]);

  const isLoadingPage = useMemo(
    () => isGetBranchEmployeesLoading || isLoading,
    [isGetBranchEmployeesLoading, isLoading]
  );

  // --- Memoized context values ---
  const settingsValue = useMemo(
    () => ({
      initialTimes,
      timeFormat,
      setTimeFormat,
      branchTimezone,
      setBranchTimezone,
      branchEmployees,
      setBranchEmployees,
      lastStoredBranchEmployees: lastStoredBranchEmployees.current,
      setLastStoredBranchEmployees,
      dateString,
      isLoading,
      setIsLoading,
      isLoadingPage,
      verticalScrollContainerRef,
    }),
    [
      initialTimes,
      timeFormat,
      branchTimezone,
      branchEmployees,
      setLastStoredBranchEmployees,
      dateString,
      isLoading,
      isLoadingPage,
    ]
  );

  const teamValue = useMemo(
    () => ({
      employeeDetails,
      setEmployeeDetails,
      selectedEmployees,
      setSelectedEmployees,
      allEmployees,
      setAllEmployees,
      lastStoredEmployeeDetails: lastStoredEmployeeDetails.current,
      setLastStoredEmployeeDetails,
      branchWorkingHoursForDate,
      setBranchWorkingHoursForDate,
      financialStatistics,
      setFinancialStatistics,
    }),
    [
      employeeDetails,
      selectedEmployees,
      allEmployees,
      setLastStoredEmployeeDetails,
      branchWorkingHoursForDate,
      financialStatistics,
    ]
  );

  const individualValue = useMemo(
    () => ({
      individualCalendarData,
      setIndividualCalendarData,
      branchWorkingHoursForPeriod,
      setBranchWorkingHoursForPeriod,
      lastStoredIndividualCalendarData:
        lastStoredIndividualCalendarData.current,
      setLastStoredIndividualCalendarData,
    }),
    [
      individualCalendarData,
      branchWorkingHoursForPeriod,
      setLastStoredIndividualCalendarData,
    ]
  );

  const moveRecordValue = useMemo(
    () => ({
      canMoveRecord,
      canMoveMessage,
      isModalOpen,
      setIsModalOpen,
      notificationModal,
      setNotificationModalSubmitHandler,
      setNotificationModalCancelHandler,
      updateCalendarDataWithNewRecord,
      checkIfCanMoveRecord,
    }),
    [
      canMoveRecord,
      canMoveMessage,
      isModalOpen,
      setIsModalOpen,
      notificationModal,
      setNotificationModalSubmitHandler,
      setNotificationModalCancelHandler,
      updateCalendarDataWithNewRecord,
      checkIfCanMoveRecord,
    ]
  );

  return (
    <CalendarSettingsContext.Provider value={settingsValue}>
      <TeamCalendarContext.Provider value={teamValue}>
        <IndividualCalendarContext.Provider value={individualValue}>
          <CalendarMoveRecordContext.Provider value={moveRecordValue}>
            {children}
          </CalendarMoveRecordContext.Provider>
        </IndividualCalendarContext.Provider>
      </TeamCalendarContext.Provider>
    </CalendarSettingsContext.Provider>
  );
};
